import { Injectable, inject } from '@angular/core';
import { Store } from '@ngxs/store';
import { FeatureFlagPurePipe } from '@wilson/feature-flags';
import {
  QualificationName,
  Shift,
  ShiftValidationDetails,
  UnassignedShift,
} from '@wilson/interfaces';
import { LocalizationState } from '@wilson/localization';
import { ShiftValidationErrorService } from '@wilson/shifts';
import {
  Observable,
  ReplaySubject,
  combineLatest,
  filter,
  map,
  shareReplay,
  switchMap,
} from 'rxjs';
import { ValidationDataSourceService } from '../../../services/validation-data-source.service';
import { format } from 'date-fns';
import { DateTimeFormat } from '@wilson/utils';

interface ValidationStateStreams {
  workingTimeValidations$: Observable<ShiftValidationDetails[] | undefined>;
  professionValidations$: Observable<ShiftValidationDetails[] | undefined>;
  shiftLogicValidations$: Observable<ShiftValidationDetails[] | undefined>;
  qualificationNames$: Observable<QualificationName[] | undefined>;
  isShiftValid$: Observable<boolean | undefined>;
}

@Injectable()
export class ShiftCardTooltipStateService {
  private readonly shiftValidationErrorService = inject(
    ShiftValidationErrorService,
  );
  private readonly featureFlagPurePipe = inject(FeatureFlagPurePipe);
  private validationDataSourceService = inject(ValidationDataSourceService);
  private readonly store = inject(Store);

  private currentUserSubject = new ReplaySubject<string | null>(1);
  private currentShiftSubject = new ReplaySubject<Shift | UnassignedShift>(1);
  private shiftValidations$!: Observable<ShiftValidationDetails[] | undefined>;

  hasShiftBeenValidated$!: Observable<boolean>;
  isLoadingValidationsForUser$!: Observable<boolean>;
  isLoadingValidationsError$!: Observable<boolean | undefined>;
  isLoadingValidationsV1Error$!: Observable<boolean | undefined>;
  workingTimeValidations$!: Observable<ShiftValidationDetails[] | undefined>;
  professionValidations$!: Observable<ShiftValidationDetails[] | undefined>;
  shiftLogicValidations$!: Observable<ShiftValidationDetails[] | undefined>;
  qualificationNames$!: Observable<QualificationName[] | undefined>;
  isShiftValid$!: Observable<boolean | undefined>;
  isShiftValidationLoading$!: Observable<boolean>;
  currentShift$ = this.currentShiftSubject.asObservable();

  constructor() {
    this.hasShiftBeenValidated$ = combineLatest([
      this.currentShiftSubject.pipe(filter((shift) => !!shift)),
    ]).pipe(
      switchMap(([currentShift]) => {
        if (!currentShift.userId) {
          return this.validationDataSourceService.hasShiftValidated(
            currentShift.id as string,
          );
        } else {
          return this.validationDataSourceService.isShiftOfUserValidated(
            currentShift.userId as string,
            new Date(
              format(
                new Date(currentShift.startDatetime as string),
                DateTimeFormat.DatabaseDateFormat,
              ),
            ),
          );
        }
      }),
    );

    this.isLoadingValidationsForUser$ = this.currentUserSubject.pipe(
      switchMap((currentUserId: string | null) =>
        this.validationDataSourceService.isCurrentUserValidationLoading(
          currentUserId || '',
        ),
      ),
    );

    this.isShiftValidationLoading$ = this.currentShiftSubject.pipe(
      switchMap((shift: Shift | UnassignedShift) => {
        if (shift.userId) {
          return this.validationDataSourceService.isShiftValidationLoadingForAssignedShifts(
            shift.id as string,
          );
        } else {
          return this.validationDataSourceService.isShiftValidationLoadingForUnassignedShifts(
            shift.id as string,
          );
        }
      }),
    );
    this.shiftValidations$ = this.currentShiftSubject.pipe(
      switchMap((shift: Shift | UnassignedShift) => {
        if (shift.userId) {
          return this.validationDataSourceService.shiftValidationStreamForAssignedShifts(
            shift.id as string,
          );
        } else {
          return this.validationDataSourceService.shiftValidationStreamForUnassignedShifts(
            shift.id as string,
          );
        }
      }),
    );

    this.isLoadingValidationsError$ =
      this.validationDataSourceService.isLoadingValidationsError();

    this.isLoadingValidationsV1Error$ =
      this.validationDataSourceService.isLoadingValidationsV1Error();

    const shiftValidationStateStreams = this.currentShiftSubject.pipe(
      map((shift: Shift | UnassignedShift) =>
        this.createValidationStateStreams(shift),
      ),
      shareReplay(1),
    );

    this.workingTimeValidations$ = shiftValidationStateStreams.pipe(
      switchMap(({ workingTimeValidations$ }) => workingTimeValidations$),
    );

    this.professionValidations$ = shiftValidationStateStreams.pipe(
      switchMap(({ professionValidations$ }) => professionValidations$),
    );

    this.shiftLogicValidations$ = shiftValidationStateStreams.pipe(
      switchMap(({ shiftLogicValidations$ }) => shiftLogicValidations$),
    );

    this.qualificationNames$ = shiftValidationStateStreams.pipe(
      switchMap(({ qualificationNames$ }) => qualificationNames$),
    );

    this.isShiftValid$ = shiftValidationStateStreams.pipe(
      switchMap(({ isShiftValid$ }) => isShiftValid$),
    );
  }

  initState(userId: string | null, shift: Shift | UnassignedShift): void {
    this.currentUserSubject.next(userId);
    this.currentShiftSubject.next(shift);
  }

  createValidationStateStreams(
    shift: Shift | UnassignedShift,
  ): ValidationStateStreams {
    const shiftValidations$ = shift.userId
      ? this.validationDataSourceService.shiftValidationStreamForAssignedShifts(
          shift.id as string,
        )
      : this.validationDataSourceService.shiftValidationStreamForUnassignedShifts(
          shift.id as string,
        );

    const isUserApprovalEnabled$ = this.featureFlagPurePipe.transform(
      'portal-shift-validation-user-approval',
    );

    const isProfessionValidationEnabled$ = this.featureFlagPurePipe.transform(
      'portal-shift-validation-user-professions',
    );

    const workingTimeValidations$ = combineLatest([
      shiftValidations$,
      isUserApprovalEnabled$,
    ]).pipe(
      map(([shiftValidations, isUserApprovalEnabled]) => {
        if (!shiftValidations) return;
        return ShiftValidationErrorService.getShiftWorkingTimeValidation(
          shiftValidations,
          isUserApprovalEnabled,
        );
      }),
    );

    const professionValidations$ = combineLatest([
      shiftValidations$,
      isProfessionValidationEnabled$,
    ]).pipe(
      map(([shiftValidations, isProfessionValidationEnabled]) => {
        if (!shiftValidations) return;
        return this.shiftValidationErrorService.getShiftProfessionValidation(
          shiftValidations,
          isProfessionValidationEnabled,
        );
      }),
    );

    const shiftLogicValidations$ = this.shiftValidations$.pipe(
      map((shiftValidations) => {
        if (!shiftValidations) return;
        return this.shiftValidationErrorService.getShiftLogicValidation(
          shiftValidations,
        );
      }),
    );

    const entityLang$ = this.store.select(LocalizationState.getEntityLang);
    const qualificationNames$ = combineLatest([
      this.featureFlagPurePipe.transform('quick-check-qualification-v2'),
      entityLang$,
    ]).pipe(
      switchMap(([quickCheckQualificationV2FeatureFlagEnabled, entityLang]) =>
        this.shiftValidations$.pipe(
          map((shiftValidations) => {
            if (!shiftValidations) return;

            return quickCheckQualificationV2FeatureFlagEnabled
              ? this.shiftValidationErrorService.getQualificationV2Name(
                  shiftValidations,
                  entityLang,
                )
              : this.shiftValidationErrorService.getQualificationName(
                  shiftValidations,
                );
          }),
        ),
      ),
    );

    const isShiftValid$ = combineLatest([
      this.workingTimeValidations$.pipe(
        map((validationDetails) =>
          validationDetails?.every((item) => item.valid),
        ),
      ),
      this.shiftLogicValidations$.pipe(
        map((validationDetails) =>
          validationDetails?.every((item) => item.valid),
        ),
      ),
      this.qualificationNames$.pipe(
        map((validationDetails) =>
          validationDetails?.every((item) => item.valid),
        ),
      ),
      this.professionValidations$.pipe(
        map((validationDetails) =>
          validationDetails?.every((item) => item.valid),
        ),
      ),
    ]).pipe(
      map(
        ([
          workingTimeValidations,
          shiftLogicValidations,
          qualificationNames,
          professionValidations,
        ]) =>
          workingTimeValidations &&
          shiftLogicValidations &&
          qualificationNames &&
          professionValidations,
      ),
    );

    return {
      workingTimeValidations$,
      professionValidations$,
      shiftLogicValidations$,
      qualificationNames$,
      isShiftValid$,
    };
  }
}
